/**
 * Loading placeholders — the shared "content is loading" shapes.
 *
 * Three shapes, because those are the three things Compass pages actually wait
 * on. Keep the surrounding chrome (headers, tabs, filters) visible and swap
 * only the data region, so the page does not jump when the data arrives:
 *
 *   - `LoadingShimmer`     — N generic bars. Card bodies, panels, lists.
 *   - `TableRowsSkeleton`  — real `<tr>/<td>` rows. Use INSIDE a `<tbody>`, so
 *                            column widths hold instead of collapsing to a
 *                            single "Loading…" cell.
 *   - `StatTilesSkeleton`  — the KPI tile row above the explorer charts, which
 *                            otherwise renders as an empty grid and pops in.
 *
 * All three animate via the shared `.compass-shimmer` class (index.css) and
 * all three are `role="status" aria-busy="true"` with a label, so a screen
 * reader announces the wait — the ad-hoc `{loading && <p>Loading…</p>}` blocks
 * these replaced announced nothing.
 *
 * Colour comes from `--bg-tertiary`/`--bg-secondary` via that class. Do NOT
 * hardcode a flat `--bg-secondary` background: that is the `<Card>` colour, so
 * the bars vanish on the cards they most often sit inside (the bug this file
 * shipped with), and it breaks on the non-default themes.
 * See docs/Features/WeeklyBriefingReport/UIAutoPolish/UI_AUTO_POLISH.md.
 */

import type { CSSProperties } from 'react';

export interface LoadingShimmerProps {
  /** Number of shimmering rows (default 3). */
  rows?: number;
  /** Row height in px (default 32). */
  rowHeight?: number;
  /** Accessible label announced to screen readers (default "Loading"). */
  label?: string;
  style?: CSSProperties;
  'data-testid'?: string;
}

export function LoadingShimmer({
  rows = 3,
  rowHeight = 32,
  label = 'Loading',
  style,
  ...rest
}: LoadingShimmerProps) {
  return (
    <div
      className="flex flex-col gap-2 py-4"
      role="status"
      aria-busy="true"
      aria-label={label}
      style={style}
      data-testid={rest['data-testid'] ?? 'loading-shimmer'}
    >
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="compass-shimmer" style={{ height: rowHeight }} />
      ))}
    </div>
  );
}

export interface TableRowsSkeletonProps {
  /** Number of placeholder rows (default 5). */
  rows?: number;
  /** Number of cells per row — match the table's column count. */
  cols: number;
  label?: string;
  'data-testid'?: string;
}

/**
 * Placeholder rows for a table body. Renders real cells so the header's column
 * widths survive the wait; the single `<td colSpan={n}>Loading…</td>` pattern
 * this replaces collapsed every column to one and then snapped back.
 *
 * Only the first row carries the status role — one announcement per table, not
 * one per row.
 */
export function TableRowsSkeleton({
  rows = 5,
  cols,
  label = 'Loading rows',
  ...rest
}: TableRowsSkeletonProps) {
  return (
    <>
      {Array.from({ length: rows }, (_, r) => (
        <tr
          key={r}
          {...(r === 0
            ? {
                role: 'status' as const,
                'aria-busy': true,
                'aria-label': label,
                'data-testid': rest['data-testid'] ?? 'table-rows-skeleton',
              }
            : { 'aria-hidden': true })}
        >
          {Array.from({ length: cols }, (_, c) => (
            <td key={c} style={{ padding: '10px 12px' }}>
              <div className="compass-shimmer" style={{ height: 14 }} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

export interface StatTilesSkeletonProps {
  /** Number of tiles (default 4 — the explorer KPI row). */
  count?: number;
  /** Tile height in px (default 98, matching the p-6 summary cards). */
  height?: number;
  label?: string;
  'data-testid'?: string;
}

/**
 * Placeholder tiles for a KPI/stat row. The grid matches the explorer summary
 * rows (`grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4`) so the real tiles
 * drop into the same footprint — these pages previously rendered an empty grid
 * while loading, which shifted everything below it down on arrival.
 */
export function StatTilesSkeleton({
  count = 4,
  height = 98,
  label = 'Loading metrics',
  ...rest
}: StatTilesSkeletonProps) {
  return (
    <div
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
      role="status"
      aria-busy="true"
      aria-label={label}
      data-testid={rest['data-testid'] ?? 'stat-tiles-skeleton'}
    >
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="compass-shimmer" style={{ height }} />
      ))}
    </div>
  );
}
